/**
 * ConfirmDischargeModal Component
 * Pre-submit confirmation modal showing totals and discharge summary
 */

import { useState } from 'react';
import {
  ExclamationTriangleIcon,
  DocumentCheckIcon,
  PaperClipIcon,
  CheckCircleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathSpinner } from '../../../shared/OathUI';
import { formatCurrencyWithSymbol } from '../utils/formatters';

const COLLAPSE_THRESHOLD = 6;

const PowerRow = ({ power, hasProof, showProofCol }) => (
  <tr className="border-b border-gray-100 dark:border-gray-700/50 last:border-b-0">
    <td className="px-2.5 py-1.5 qhd:px-3 qhd:py-2">
      <span className="font-mono font-semibold text-xs qhd:text-sm text-primary dark:text-primary-light">
        {power.powerNo}
      </span>
    </td>
    <td className="px-2.5 py-1.5 qhd:px-3 qhd:py-2 text-xs qhd:text-sm text-gray-700 dark:text-gray-300 truncate max-w-[8rem] sm:max-w-[12rem]" title={power.defendant}>
      {power.defendant || '\u2014'}
    </td>
    <td className="px-2.5 py-1.5 qhd:px-3 qhd:py-2 text-right text-xs qhd:text-sm font-medium text-gray-900 dark:text-white tabular-nums">
      {power.liability != null ? formatCurrencyWithSymbol(power.liability) : '\u2014'}
    </td>
    {showProofCol && (
      <td className="px-2.5 py-1.5 qhd:px-3 qhd:py-2 text-xs qhd:text-sm">
        {hasProof ? (
          <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 font-medium">
            <CheckCircleIcon className="h-3 w-3" />
            Proof
          </span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">&mdash;</span>
        )}
      </td>
    )}
  </tr>
);

const CollapsiblePowerList = ({ title, count, powers, color, borderClass, hasProofForPower, showProofCol, extraContent }) => {
  const shouldCollapse = count > COLLAPSE_THRESHOLD;
  const [expanded, setExpanded] = useState(!shouldCollapse);

  const dotClass = color === 'green' ? 'bg-green-500' : 'bg-orange-500';
  const border = borderClass || 'border-gray-200 dark:border-gray-700';

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex items-center gap-1.5 py-1.5 sm:py-0 text-xs qhd:text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1.5 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
      >
        <span className={`h-2 w-2 rounded-full ${dotClass}`} />
        {title} ({count})
        <ChevronDownIcon className={`h-3 w-3 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className={`rounded-lg border ${border} overflow-hidden max-h-40 sm:max-h-48 overflow-y-auto`}>
          <table className="min-w-full">
            <tbody>
              {powers.map((power) => (
                <PowerRow key={power.powerNo} power={power} hasProof={hasProofForPower(power.powerNo)} showProofCol={showProofCol} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {extraContent}
    </div>
  );
};

const ConfirmDischargeModal = ({
  isOpen,
  onClose,
  validationResult,
  totalLiability,
  reason,
  agentComments,
  proofsByPower = new Map(),
  powerDetailsMap = new Map(),
  onConfirm,
  isSubmitting,
}) => {
  const readyPowers = validationResult?.ready || [];
  const needsProofPowers = validationResult?.needsProof || [];
  const readyCount = readyPowers.length;
  const needsProofCount = needsProofPowers.length;
  const totalCount = readyCount + needsProofCount;

  const hasProofForPower = (powerNo) =>
    !!proofsByPower.get(powerNo)?.fileId || !!powerDetailsMap.get(powerNo)?.hasProofUploaded;

  const totalProofCount = [...readyPowers, ...needsProofPowers].filter((p) => hasProofForPower(p.powerNo)).length;
  const needsProofMissing = needsProofPowers.filter(
    (p) => !hasProofForPower(p.powerNo),
  ).length;

  const anyHasProof = totalProofCount > 0;

  return (
    <OathModal
      isOpen={isOpen}
      onClose={isSubmitting ? undefined : onClose}
      title="Confirm Discharge"
      size="xl"
      footer={
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 w-full">
          <OathButton variant="outline" onClick={onClose} disabled={isSubmitting} className="w-full sm:w-auto">
            Cancel
          </OathButton>
          <OathButton onClick={onConfirm} disabled={isSubmitting || needsProofMissing > 0} className="w-full sm:w-auto">
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <OathSpinner size="sm" />
                Submitting...
              </span>
            ) : (
              needsProofCount > 0
                ? `Confirm (${readyCount} discharge${readyCount !== 1 ? 's' : ''}, ${needsProofCount} for review)`
                : `Confirm Discharge (${totalCount})`
            )}
          </OathButton>
        </div>
      }
    >
      <div className="space-y-3">
        {/* Summary bar */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl px-4 py-3 qhd:px-5 qhd:py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-primary/10 dark:bg-primary/20 rounded-lg">
              <DocumentCheckIcon className="h-5 w-5 qhd:h-6 qhd:w-6 text-primary dark:text-primary-light" />
            </div>
            <div>
              <p className="text-xs qhd:text-sm text-gray-500 dark:text-gray-400">
                {needsProofCount > 0 ? 'Submitting' : 'Discharging'}
              </p>
              <p className="text-base qhd:text-lg font-bold text-gray-900 dark:text-white">
                {totalCount} power{totalCount !== 1 ? 's' : ''} — {formatCurrencyWithSymbol(totalLiability)}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs qhd:text-sm">
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <span className="h-2 w-2 rounded-full bg-green-500" />
              <strong>{readyCount}</strong> ready
            </span>
            {needsProofCount > 0 && (
              <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <span className="h-2 w-2 rounded-full bg-orange-500" />
                <strong>{needsProofCount}</strong> pending review
              </span>
            )}
            {totalProofCount > 0 && (
              <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <PaperClipIcon className="h-3.5 w-3.5" />
                {totalProofCount} proof{totalProofCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Power lists — side by side when both exist */}
        <div className={`grid gap-3 ${readyCount > 0 && needsProofCount > 0 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
          {readyCount > 0 && (
            <CollapsiblePowerList
              title="Ready"
              count={readyCount}
              powers={readyPowers}
              color="green"
              hasProofForPower={hasProofForPower}
              showProofCol={anyHasProof}
            />
          )}

          {needsProofCount > 0 && (
            <CollapsiblePowerList
              title="Pending Review"
              count={needsProofCount}
              powers={needsProofPowers}
              color="orange"
              borderClass="border-orange-200 dark:border-orange-800/50"
              hasProofForPower={hasProofForPower}
              showProofCol
              extraContent={
                needsProofMissing > 0 ? (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400 font-medium">
                    {needsProofMissing} power{needsProofMissing !== 1 ? 's' : ''} still need proof uploaded.
                  </p>
                ) : null
              }
            />
          )}
        </div>

        {/* Reason & Comments — inline */}
        {(reason || agentComments) && (
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs qhd:text-sm text-gray-600 dark:text-gray-400">
            {reason && (
              <p><span className="font-semibold text-gray-800 dark:text-gray-200">Reason:</span> {reason}</p>
            )}
            {agentComments && (
              <p><span className="font-semibold text-gray-800 dark:text-gray-200">Comments:</span> {agentComments}</p>
            )}
          </div>
        )}

        {/* Warning */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50">
          <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <p className="text-xs qhd:text-sm text-amber-700 dark:text-amber-300">
            This action cannot be undone. Discharged powers will be permanently released.
          </p>
        </div>
      </div>
    </OathModal>
  );
};

export default ConfirmDischargeModal;
